import { useI18n } from '@/hooks/useI18n';
import { Skeleton } from '@/components/ui/skeleton';
import { Dot } from '@/components/primitives/Dot';
import { Empty } from '@/components/primitives/Empty';
import type { RunSummary } from '@/types/api';
import type { SchedulerRunStatus, SchedulerRunType } from '@/types/enums';
import { formatDate, formatDuration } from '@/lib/format';
import { cn } from '@/lib/cn';

const LABEL: Record<SchedulerRunType, string> = {
  MAIN_3H: 'cycle',
  NIGHTLY_PROFILE: 'profile',
  HOURLY_ROLLUP: 'rollup-h',
  DAILY_ROLLUP: 'rollup-d',
};

const VARIANT: Record<SchedulerRunStatus, 'ok' | 'running' | 'fail' | 'warn'> = {
  OK: 'ok',
  RUNNING: 'running',
  FAILED: 'fail',
  PARTIAL: 'warn',
};

interface RunsFeedProps {
  runs: (RunSummary & { type?: SchedulerRunType })[] | undefined;
  loading?: boolean;
}

export function RunsFeed({ runs, loading }: RunsFeedProps) {
  const { t } = useI18n();
  return (
    <section className="space-y-2">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[14px] font-semibold tracking-tight text-text-1">Runs</h3>
        <span className="text-[11px] text-text-3">
          {t('Flux live · 20 derniers', 'Live feed · latest 20')}
        </span>
      </header>
      <div className="overflow-hidden rounded-[14px] border border-line-1 bg-bg-1 shadow-card">
        {loading ? (
          <div className="space-y-px p-0.5">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-none" />
            ))}
          </div>
        ) : (runs ?? []).length === 0 ? (
          <div className="p-5">
            <Empty
              title={t('Aucun run', 'No runs')}
              description={t('Aucune exécution enregistrée.', 'No recorded runs.')}
            />
          </div>
        ) : (
          <div className="max-h-[420px] divide-y divide-line-1/70 overflow-y-auto">
            {(runs ?? []).map((r) => {
              const variant = VARIANT[r.status];
              const label = r.type ? LABEL[r.type] : 'run';
              const time = formatDate(r.startedAt, 'HH:mm:ss');
              return (
                <div
                  key={r.runId}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 px-3 py-2.5 text-[11px] transition-colors hover:bg-bg-2/70 sm:min-h-9 sm:grid-cols-[80px_16px_96px_minmax(0,1fr)_auto] sm:py-1.5"
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-2 sm:contents">
                    <span className="font-mono tabular-nums text-text-3">{time}</span>
                    <Dot variant={variant} pulse={variant === 'running'} />
                    <span className="text-text-2">{label}</span>
                  </div>
                  <span className="col-span-2 row-start-2 text-text-3 sm:col-span-1 sm:row-auto sm:truncate">
                    <span className="text-emerald">{r.accountsOk} ok</span>
                    <span className="text-text-4"> · </span>
                    <span className={cn(r.accountsFailed > 0 ? 'text-rose' : 'text-text-3')}>
                      {r.accountsFailed} err
                    </span>
                    <span className="text-text-4"> · </span>
                    {r.accountsAttempted} total
                  </span>
                  <span
                    className={cn(
                      'col-start-2 row-start-1 font-mono tabular-nums sm:col-auto sm:row-auto',
                      variant === 'fail'
                        ? 'text-rose'
                        : variant === 'warn'
                          ? 'text-amber'
                          : 'text-text-3',
                    )}
                  >
                    {formatDuration(r.durationMs)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
